// Section Paramètres → Modules optionnels.
// Permet d'activer/désactiver le Module Immobilisations (add-on Stripe 6 000 XPF/mois).
import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatErreur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Building2, Loader2, CheckCircle2 } from "lucide-react";

export default function ModulesSection() {
  const { abonnement, chargerAbonnement } = useAuth();
  const actif = !!abonnement?.module_immobilisations;
  const [envoi, setEnvoi] = useState(false);

  const basculer = async (activer) => {
    setEnvoi(true);
    try {
      const action = activer ? "activer" : "desactiver";
      await api.post(`/abonnements/modules/immobilisations/${action}`);
      toast.success(activer ? "Module Immobilisations activé." : "Module désactivé (effectif en fin de période).");
      if (chargerAbonnement) await chargerAbonnement();
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail) || "Opération impossible.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div data-testid="modules-section" className="space-y-4">
      <div className="flex items-start justify-between gap-4 p-4 rounded-lg border border-[#4A8A8A]/20 bg-[#E8F4F4]/40">
        <div className="flex gap-3">
          <div className="h-11 w-11 rounded-lg bg-white flex items-center justify-center shrink-0">
            <Building2 className="h-6 w-6 text-[#1D3D3D]" />
          </div>
          <div>
            <h3 className="font-bold text-[#1D3D3D] flex items-center gap-2">
              Module Immobilisations — 6 000 XPF/mois
              {actif && (
                <span data-testid="module-immo-actif" className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Activé
                </span>
              )}
            </h3>
            <p className="text-sm text-[#1D3D3D]/70 mt-1">
              Gérez vos immobilisations, calculez les amortissements (linéaire / dégressif) et générez
              automatiquement les écritures de dotation.
            </p>
          </div>
        </div>
        {actif ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="bouton-desactiver-module" variant="outline" disabled={envoi} className="border-red-200 text-red-600 shrink-0">
                Désactiver
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Désactiver le Module Immobilisations ?</AlertDialogTitle>
                <AlertDialogDescription>
                  La facturation de 6 000 XPF/mois s'arrêtera. La désactivation est effective à la fin
                  de la période en cours. Vos données d'immobilisations sont conservées.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction data-testid="confirmer-desactivation" onClick={() => basculer(false)} className="bg-red-600 hover:bg-red-700">
                  Désactiver le module
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="bouton-activer-module" disabled={envoi} className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white shrink-0">
                {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activer ce module"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Activer le Module Immobilisations ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Ce module sera facturé <strong>6 000 XPF/mois</strong> en plus de votre abonnement actuel. Confirmer ?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction data-testid="confirmer-activation" onClick={() => basculer(true)} className="bg-[#1D3D3D] hover:bg-[#2D5A5A]">
                  Confirmer l'activation
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}
